import { useState, type FormEvent } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useSignUp } from "@clerk/react/legacy";
import { motion } from "motion/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import logoLight from "@/assets/png/logo.png";
import logoDark from "@/assets/png/logo_reversed.png";

// Aurora gradients (same as DashboardPage)
const auroraGradientDark = [
  "radial-gradient(ellipse at 50% 0%, oklch(0.15 0.05 250) 0%, transparent 50%)",
  "radial-gradient(ellipse at 30% 40%, oklch(0.35 0.15 285) 0%, transparent 40%)",
  "radial-gradient(ellipse at 70% 50%, oklch(0.45 0.2 350) 0%, transparent 40%)",
  "linear-gradient(to top, oklch(0.5 0.25 30) 0%, transparent 50%)",
].join(", ");

const auroraGradientLight = [
  "radial-gradient(ellipse at 50% 0%, oklch(0.85 0.04 250) 0%, transparent 50%)",
  "radial-gradient(ellipse at 30% 40%, oklch(0.82 0.08 285) 0%, transparent 40%)",
  "radial-gradient(ellipse at 70% 50%, oklch(0.88 0.1 350) 0%, transparent 40%)",
  "radial-gradient(ellipse at 50% 70%, oklch(0.9 0.1 30) 0%, transparent 40%)",
  "linear-gradient(to top, oklch(0.9 0.1 30) 10%, transparent 40%)",
].join(", ");

export function SignUpPage() {
  const navigate = useNavigate();
  const { signUp, isLoaded, setActive } = useSignUp();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleGoogleSignUp = async () => {
    await signUp?.authenticateWithRedirect({
      strategy: "oauth_google",
      redirectUrl: "/sso-callback",
      redirectUrlComplete: "/dashboard",
    });
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!isLoaded) return;

    setLoading(true);
    setError("");

    try {
      const result = await signUp.create({
        emailAddress: email,
        password,
      });

      // Check if email verification is required
      if (result.status === "complete") {
        await setActive({ session: result.createdSessionId });
        navigate("/dashboard");
      } else if (result.status === "missing_requirements") {
        // Handle email verification flow
        setError("Please check your email to verify your account");
      }
    } catch (err: any) {
      setError(
        err.errors?.[0]?.message ||
          "Failed to create account. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const passwordRequirements = [
    { text: "at least 8 characters", met: password.length >= 8 },
    { text: "a number (0-9)", met: /\d/.test(password) },
  ];

  return (
    <div className="flex min-h-screen">
      {/* Left Panel - Form */}
      <div className="flex w-full flex-col justify-center px-8 lg:w-1/2 lg:px-16">
        <div className="mx-auto w-full max-w-[430px]">
          {/* Logo */}
          <Link to="/" className="mb-8 flex items-center gap-2">
            <img src={logoLight} alt="Rayloop" className="h-8 dark:hidden" />
            <img
              src={logoDark}
              alt="Rayloop"
              className="hidden h-8 dark:block"
            />
          </Link>

          {/* Header */}
          <div className="mb-8">
            <h1 className="font-display text-3xl font-bold">
              Create your account
            </h1>
          </div>

          {/* OAuth Buttons */}
          <div className="mb-6 flex flex-col gap-3">
            <Button
              variant="outline"
              onClick={handleGoogleSignUp}
              className="w-full justify-center gap-2 bg-secondary cursor-pointer hover:bg-secondary/80"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24">
                <path
                  fill="currentColor"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="currentColor"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="currentColor"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="currentColor"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              Continue with Google
            </Button>
          </div>

          {/* Divider */}
          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">
                OR
              </span>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                className="bg-transparent"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Create a password"
                required
                className="bg-transparent"
              />
              {password && (
                <div className="text-muted-foreground mt-2 space-y-1 text-xs">
                  <p className="font-medium">Password must contain:</p>
                  {passwordRequirements.map((req, i) => (
                    <p key={i} className={req.met ? "text-green-500" : ""}>
                      • {req.text}
                    </p>
                  ))}
                </div>
              )}
            </div>

            {error && (
              <div className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
                {error}
              </div>
            )}

            <p className="text-muted-foreground text-xs">
              By continuing, you agree to the{" "}
              <Link to="/terms" className="underline hover:text-foreground">
                Terms of Service
              </Link>{" "}
              and{" "}
              <Link to="/privacy" className="underline hover:text-foreground">
                Privacy Policy
              </Link>
              .
            </p>

            <Button
              type="submit"
              className="w-full bg-foreground text-background hover:bg-foreground/90 cursor-pointer"
              disabled={loading}
            >
              {loading ? "Creating account..." : "Create your account"}
            </Button>
          </form>

          {/* Footer Links */}
          <p className="text-muted-foreground mt-6 text-center text-sm">
            Already have an account?{" "}
            <Link
              to="/login"
              className="text-foreground underline hover:text-brand cursor-pointer"
            >
              Log in
            </Link>
          </p>
        </div>
      </div>

      {/* Right Panel - Aurora Gradient */}
      <div className="relative hidden lg:block lg:w-1/2 p-4">
        <div className="relative h-full w-full overflow-hidden rounded-xl bg-white dark:bg-[#1c1c1c]">
          {/* Aurora gradient — light mode */}
          <div
            className="pointer-events-none absolute inset-0 dark:hidden"
            style={{ background: auroraGradientLight }}
          />
          {/* Aurora gradient — dark mode */}
          <div
            className="pointer-events-none absolute inset-0 hidden dark:block"
            style={{ background: auroraGradientDark }}
          />

          {/* Interactive Prompt Component */}
          <div className="relative z-10 flex h-full items-center justify-center p-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="w-full max-w-md"
            >
              <div className="group relative">
                {/* Glow effect */}
                <div className="absolute -inset-px rounded-2xl bg-gradient-to-r from-[oklch(0.55_0.25_285_/_0.25)] via-transparent to-[oklch(0.55_0.25_285_/_0.25)] opacity-60 blur-sm" />

                <div className="bg-card/80 border-border/50 relative rounded-2xl border shadow-lg backdrop-blur p-6">
                  <p className="text-muted-foreground text-sm mb-3">
                    Start building with
                  </p>
                  <h2 className="font-display text-foreground text-3xl font-bold mb-4">
                    Rayloop AI
                  </h2>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    Create powerful AI workflows with our visual pipeline
                    editor. Connect models, chain operations, and build
                    reproducible content generation pipelines.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}
